import React, { useContext } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography } from '@mui/material';
import { OverallTradingMetrics } from '../interfaces/Metrics';
import { SettingsContext } from '../context/SettingsContext';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PercentIcon from '@mui/icons-material/Percent';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useTranslation } from '../context/TranslationContext';

const getColor = (key: string, value: number | string, darkMode: boolean) => {
  const green = darkMode ? '#089981' : 'green';
  const red = darkMode ? '#FF6347' : 'red';

  if (typeof value === 'number') {
    if (key === 'totalNetProfit') return value >= 1 ? green : red;
    if (key === 'grossProfit') return value >= 1 ? green : red;
    if (key === 'grossLoss') return value >= 1 ? green : red;
    // if (key === 'profitFactor') return value >= 1 ? green : red;
    if (key === 'maxConsLosers') return red;
    if (key === 'maxConsWinners') return green;
    if (key === 'maxDrawdown') return red;
    if (key === 'maxDrawdownDuration') return red;
    if (key === 'maxIntradayDrawdown') return red;
    if (key === 'maxIntradayDrawdownDuration') return red;
    if (key === 'maxRunup') return green;
    if (key === 'maxRunupDuration') return green;
    if (key === 'winningTrades') return value >= 0 ? green : red;
    if (key === 'losingTrades') return value >= 0 ? green : red;
    if (key === 'avgTradeNetProfit') return value >= 0 ? green : red;
    if (key === 'avgWinningTrade') return value >= 0 ? green : red;
    if (key === 'avgLosingTrade') return value >= 0 ? green : red;
    if (key === 'largestWin') return value >= 0 ? green : red;
    if (key === 'largestLoss') return value >= 0 ? green : red;
    if (key === 'profitPerLosingTrade') return value >= 0 ? green : red;
    if (key === 'lossPerWinningTrade') return value >= 0 ? green : red;
    if (key === 'lossPerLosingTrade') return value >= 0 ? green : red;
    if (key === 'annualRateOfReturn') return value >= 0 ? green : red;
    if (key === 'sharpeRatio') return value >= 1 ? green : red;
    if (key === 'sortinoRatio') return value >= 1 ? green : red;
    if (key === 'sterlingRatio') return value >= 1 ? green : red;
    if (key === 'marRatio') return value >= 1 ? green : red;
    if (key === 'efficiencyFactor') return value >= 1 ? green : red;
    if (key === 'returnOnInitialCapital') return value >= 0 ? green : red;
    if (key === 'marginRequirements') return value >= 0 ? green : red;
    if (key === 'maxDrawdownAsPercentOfInitialEquity') return red;
    if (key === 'maxDrawdownAsPercentOfTotalEquity') return red;
    if (key === 'maxIntradayDrawdownAsPercentOfInitialEquity') return red;
    if (key === 'maxIntradayDrawdownAsPercentOfTotalEquity') return red;
    if (key === 'recoveryFactor') return value >= 1 ? green : red;
    if (key === 'npMaxDD') return value >= 1 ? green : red;
    if (key === 'npIntradayMaxDD') return value >= 1 ? green : red;
    if (key === 'avgWorst5DD') return red;
    if (key === 'maxRunupAsPercentOfInitialEquity') return green;
    if (key === 'maxRunupAsPercentOfTotalEquity') return green;
    if (key === 'maxIntradayRunupAsPercentOfInitialEquity') return green;
    if (key === 'maxIntradayRunupAsPercentOfTotalEquity') return green;
    if (key === 'maxIntradayRunupDuration') return green;
    if (key === 'maxIntradayRunup') return green;
    if (key === 'maxIntradayRunupDate') return green;

    return undefined;
  }
  return undefined;
};

const getDataCellStyle = (metric: string, value: number | string, darkMode: boolean) => {
  let result: React.CSSProperties = {
    minWidth: '230px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: getColor(metric, value, darkMode)
  };
  switch (metric) {
    case 'initialCapital':
      result = {
        ...result,
      }
      break;
  }
      
  return result;
};

const iconStyle = { fontSize: '1rem', verticalAlign: 'middle', marginRight: '4px', marginBottom: '3px' };

const TradingMetricsSmall: React.FC<{ metrics: OverallTradingMetrics }> = ({ metrics }) => {
  const { darkMode, getLocale, showCurrencySymbol, getCurrencySymbol } = useContext(SettingsContext);
  
  const formatValue = (key: string, value: number | string, decimals: number) => {
    if (typeof value === 'number') {
      if (key.toLowerCase().includes('trades') || key.toLowerCase().includes('count')) {
        return value.toLocaleString(getLocale(), { minimumFractionDigits: 0, maximumFractionDigits: 0 }); // Use integers for trade counts
      }
      return value.toLocaleString(getLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals }); // Use specified decimals for other numeric values
    }
    return value;
  };

  const formatValueWithCurrency = (key: string, value: number | string, decimals: number) => {
    const formattedValue = formatValue(key, value, decimals);
    if (typeof value === 'number' && showCurrencySymbol) {
      return `${formattedValue} ${getCurrencySymbol()}`;
    }
    return formattedValue;
  };

  const { t } = useTranslation();
  const decimals = 2;
  const firstColumnStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  if (!metrics) return <div>Error: No metrics available</div>;

  return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><strong>{t("Metric")}</strong></Typography></TableCell>
              <TableCell align='right'><Typography><strong>{t("Value")}</strong></Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Initial Capital")}</strong></Typography></TableCell>
              <TableCell align='right' style={getDataCellStyle('initialCapital', metrics.initialCapital, darkMode)}>
                <Typography>{formatValueWithCurrency('initialCapital', metrics.initialCapital, decimals)}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Total Net Profit")}</strong></Typography></TableCell>
              <TableCell align='right' style={getDataCellStyle('totalNetProfit', metrics.totalNetProfit, darkMode)}>
                <Typography>{formatValueWithCurrency('totalNetProfit', metrics.totalNetProfit, decimals)}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><BarChartIcon style={iconStyle} /><strong>{t("Total Trades")}</strong></Typography></TableCell>
              <TableCell align='right' style={getDataCellStyle('totalTrades', metrics.totalTrades, darkMode)}>
                <Typography>{formatValue('totalTrades', metrics.totalTrades, decimals)}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><PercentIcon style={iconStyle} /><strong>{t("Percent Profitable Trades")}</strong></Typography></TableCell>
              <TableCell align='right' style={getDataCellStyle('percentProfitable', metrics.percentProfitable, darkMode)}>
                <Typography>{formatValue('percentProfitable', metrics.percentProfitable, decimals)} %</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><TrendingUpIcon style={iconStyle} /><strong>{t("Profit Factor")}</strong></Typography></TableCell>
              <TableCell align='right' style={getDataCellStyle('profitFactor', metrics.profitFactor, darkMode)}>
                <Typography>{formatValue('profitFactor', metrics.profitFactor, decimals)}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><TrendingDownIcon style={iconStyle} /><strong>{t("Max Drawdown")}</strong></Typography></TableCell>
              <TableCell align='right' style={getDataCellStyle('maxDrawdown', metrics.maxDrawdown.maxDrawdown, darkMode)}>
                <Typography>{formatValueWithCurrency('maxDrawdown', metrics.maxDrawdown.maxDrawdown, decimals)}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Avg Trade Net Profit")}</strong></Typography></TableCell>
              <TableCell align='right' style={getDataCellStyle('avgTradeNetProfit', metrics.avgTradeNetProfit, darkMode)}>
                <Typography>{formatValueWithCurrency('avgTradeNetProfit', metrics.avgTradeNetProfit, decimals)}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><TrendingDownIcon style={iconStyle} /><strong>{t("Max Intraday Drawdown")}</strong></Typography></TableCell>
              <TableCell align='right' style={getDataCellStyle('maxIntradayDrawdown', metrics.maxIntradayDrawdown.maxDrawdown, darkMode)}>
                <Typography>{formatValueWithCurrency('maxIntradayDrawdown', metrics.maxIntradayDrawdown.maxDrawdown, decimals)}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Return on Initial Capital")}</strong></Typography></TableCell>
              <TableCell align='right' style={getDataCellStyle('returnOnInitialCapital', metrics.returnOnInitialCapital, darkMode)}>
                <Typography>{formatValue('returnOnInitialCapital', metrics.returnOnInitialCapital, decimals)} %</Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
  );
};

export default TradingMetricsSmall;
